import React, { useState } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  useTheme,
  Divider,
  useMediaQuery,
} from "@mui/material";
import HomeIcon from "@mui/icons-material/Home";
import ThemeToggleButton from "./ThemeToggleButton";
import { useNavigate } from "react-router-dom";
import QrCodeScannerIcon from "@mui/icons-material/QrCodeScanner";
import { useGoldRate } from "../context/GoldRateContext";
import ScannerModal from "./ScannerModal";
import GoldRateConfigModal from "./GoldRateConfigModal";
import { useContext } from "react";
import { DataContext } from "../context/DataContext";


const Navbar = ({ children, sidebarOpen = true, onItemScanned  }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isDarkMode = theme.palette.mode === "dark";
  const { stock } = useContext(DataContext); // Get stock from context
  const { showRateConfig, setShowRateConfig } = useGoldRate();
  const [scannerOpen, setScannerOpen] = useState(false);

  // Sidebar width constants - should match Sidebar component
  const SIDEBAR_WIDTH_OPEN = 290; // Matches DRAWER_WIDTH in Sidebar
  const SIDEBAR_WIDTH_CLOSED = 90; // Matches DRAWER_WIDTH_COLLAPSED in Sidebar

  // Calculate navbar width and position based on sidebar state
  const navbarWidth = sidebarOpen 
    ? `calc(100% - ${SIDEBAR_WIDTH_OPEN}px)`
    : `calc(100% - ${SIDEBAR_WIDTH_CLOSED}px)`;
  
  const navbarLeft = sidebarOpen 
    ? `${SIDEBAR_WIDTH_OPEN}px`
    : `${SIDEBAR_WIDTH_CLOSED}px`;

  // Theme colors for consistent styling
  const colors = {
    light: {
      primary: theme.palette.primary.main,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      background: 'rgba(255, 255, 255, 0.3)',
      border: 'rgba(99, 102, 241, 0.2)',
    },
    dark: {
      primary: theme.palette.primary.main,
      textPrimary: theme.palette.text.primary,
      textSecondary: theme.palette.text.secondary,
      background: 'rgba(30, 41, 59, 0.7)',
      border: 'rgba(148, 163, 184, 0.1)',
    }
  };

  const currentColors = isDarkMode ? colors.dark : colors.light;

  const handleHome = () => {
    navigate("/");
  };

  const handleScanClick = () => {
    // Check if rates are configured
    const rates = localStorage.getItem("gold_rates");
    if (!rates) {
      setShowRateConfig(true);
    } else {
      setScannerOpen(true);
    }
  };

  const handleItemScanned = (item) => {
    if (onItemScanned) {
      onItemScanned(item);
    }
    setScannerOpen(false);
  };

  return (
    <>
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        width: navbarWidth,
        left: navbarLeft,
        zIndex: theme.zIndex.drawer + 1,
        backdropFilter: "blur(20px)",
        WebkitBackdropFilter: "blur(20px)",
        backgroundColor: currentColors.background,
        borderBottom: `1px solid ${currentColors.border}`,
        boxShadow: theme.shadows[4],
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      }}
    >
      <Toolbar
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: { xs: 2, sm: 3, md: 4 },
          height: 70,
          gap: 2,
        }}
      >
        {/* Left Section - Logo & Branding */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            minWidth: 0,
            cursor: "pointer",
            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            "&:hover": {
              transform: "translateX(4px)",
            },
          }}
          onClick={handleHome}
        >
          {/* Logo Box */}
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2,
              background: `linear-gradient(135deg, ${currentColors.primary} 0%, ${theme.palette.primary.dark} 100%)`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              flexShrink: 0,
              boxShadow: isDarkMode
                ? "0 4px 14px 0 rgba(96, 165, 250, 0.35)"
                : "0 4px 14px 0 rgba(37, 99, 235, 0.4)",
              position: "relative",
              overflow: "hidden",
              "&:hover": {
                transform: "scale(1.05)",
              },
            }}
          >
            💎
          </Box>

          {/* Brand Text */}
          {!isMobile && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h5"
                sx={{
                  fontWeight: 800,
                  fontSize: "1.35rem",
                  letterSpacing: "-0.02em",
                  color: currentColors.textPrimary,
                  whiteSpace: "nowrap",
                }}
              >
                JewelStock
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  fontSize: "0.7rem",
                  display: "block",
                  fontWeight: 600,
                  color: currentColors.textSecondary,
                  letterSpacing: "0.5px",
                  textTransform: "uppercase",
                }}
              >
                Billing & Inventory
              </Typography>
            </Box>
          )}
        </Box>

        {/* Right Section - Actions */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: { xs: 0.5, sm: 1.5 },
            flexShrink: 0,
          }}
        >
         {/* Scanner Button - Add this */}
            <IconButton
              onClick={handleScanClick}
              title="Scan Item"
              size="medium"
              sx={{
                borderRadius: 12,
                backgroundColor: isDarkMode
                  ? "rgba(30, 41, 59, 0.3)"
                  : "rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(10px)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                color: currentColors.textPrimary,
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(30, 41, 59, 0.5)"
                    : "rgba(255, 255, 255, 0.8)",
                  transform: "translateY(-2px)",
                  color: currentColors.primary,
                },
              }}
            >
              <QrCodeScannerIcon sx={{ fontSize: 24 }} />
            </IconButton>

          {/* Home Button */}
          {!isMobile && (
            <IconButton
              onClick={handleHome}
              size="medium"
              title="Home"
              sx={{
                borderRadius: 12,
                backgroundColor: isDarkMode
                  ? "rgba(30, 41, 59, 0.3)"
                  : "rgba(255, 255, 255, 0.6)",
                backdropFilter: "blur(10px)",
                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                color: currentColors.textPrimary,
                "&:hover": {
                  backgroundColor: isDarkMode
                    ? "rgba(30, 41, 59, 0.5)"
                    : "rgba(255, 255, 255, 0.8)",
                  transform: "translateY(-2px)",
                  color: currentColors.primary,
                },
              }}
            >
              <HomeIcon sx={{ fontSize: 24 }} />
            </IconButton>
          )}

          {/* Theme Toggle Button */}
          <ThemeToggleButton />

          {/* Divider */}
          {!isMobile && (
            <Divider
              orientation="vertical"
              flexItem
              sx={{
                my: 1.5,
                mx: 0.5,
                borderColor: currentColors.border,
              }}
            />
          )}

        <Box 
          sx={{ 
            display: 'flex', 
            flex: 1, 
            justifyContent: 'center',
          }}
        >
          {children}
        </Box>

        </Box>
      </Toolbar>
    </AppBar>
    {/* Modals */}
      <ScannerModal
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onItemScanned={handleItemScanned}
        stock={stock} // Pass stock to scanner
      />

      <GoldRateConfigModal
        open={showRateConfig}
        onComplete={() => setScannerOpen(true)}
      />
    </>
  );
};

export default Navbar;